import { CookieHandler, CookieHandlerInstance } from './cookiehandler.js';
import {
  ADDED_TO_WISHLIST_EVENT,
  config,
  LOGGED_OUT_EVENT,
  REMOVE_COUNT_FROM_WISHLIST_EVENT,
  REMOVED_FROM_WISHLIST_EVENT,
} from './config.js';
import { WishlistCountContainer, WishlistDataContainer } from '../../@types/wishlist.js';
import { getSafeNumber } from './get-safe-number.js';

const COOKIE_NAME = 'ents_wishlist_count';

export class WishlistHeaderCountService {
  private readonly cookieHandler: CookieHandler;

  declare private readonly onAddedToWishlist: (_: CustomEvent<WishlistDataContainer>) => void;

  declare private readonly onRemovedFromWishlist: (_: CustomEvent<WishlistDataContainer>) => void;

  declare private readonly onRemovedCountFromWishlist: (data: CustomEvent<WishlistCountContainer>) => void;

  declare private readonly onLoggedOut: (_: Event) => void;

  declare private readonly onPageShownFromBFCache: (_: PageTransitionEvent) => void;

  constructor(cookieHandler: CookieHandler) {
    this.cookieHandler = cookieHandler;
    this.onAddedToWishlist = (): void => {
      this.updateCount(this.getCount() + 1);
    };
    this.onRemovedFromWishlist = (): void => {
      this.updateCount(this.getCount() - 1);
    };
    this.onRemovedCountFromWishlist = (data) => {
      this.updateCount(this.getCount() - data.detail.count);
    };
    this.onLoggedOut = (): void => {
      this.updateCount(0);
      this.deleteCookie();
    };
    this.onPageShownFromBFCache = (e): void => {
      if (e.persisted) {
        this.updateCount(this.getCount());
      }
    };
  }

  init() {
    this.destroy();
    document.addEventListener(ADDED_TO_WISHLIST_EVENT, this.onAddedToWishlist);
    document.addEventListener(REMOVED_FROM_WISHLIST_EVENT, this.onRemovedFromWishlist);
    document.addEventListener(LOGGED_OUT_EVENT, this.onLoggedOut);
    document.addEventListener(REMOVE_COUNT_FROM_WISHLIST_EVENT, this.onRemovedCountFromWishlist);
    window.addEventListener('pageshow', this.onPageShownFromBFCache);

    const initialCount = getSafeNumber(config.displayedItemCount);
    if (initialCount !== null) {
      this.updateCount(initialCount);
    } else {
      const cookieCount = this.getCount();
      this.updateCount(cookieCount);
    }
  }

  destroy() {
    document.removeEventListener(ADDED_TO_WISHLIST_EVENT, this.onAddedToWishlist);
    document.removeEventListener(REMOVED_FROM_WISHLIST_EVENT, this.onRemovedFromWishlist);
    document.removeEventListener(LOGGED_OUT_EVENT, this.onLoggedOut);
    document.removeEventListener(REMOVE_COUNT_FROM_WISHLIST_EVENT, this.onRemovedCountFromWishlist);
    window.removeEventListener('pageshow', this.onPageShownFromBFCache);
  }

  getCount(): number {
    const c = this.cookieHandler.getCookie(COOKIE_NAME);
    const count = getSafeNumber(c);
    return count ?? 0;
  }

  updateCount(count: number) {
    const newCount = Math.max(count, 0);
    WishlistHeaderCountService.updateHeaderCount(newCount);
    this.updateCookie(newCount);
  }

  static updateHeaderCount(count: number) {
    const headerSpans = document.querySelectorAll('[data-suchen-header-wunschliste-badge]');
    headerSpans.forEach((hSpan: Element) => {
      if (hSpan !== null) {
        if (count > 0) {
          hSpan.innerHTML = `${count}`;
          hSpan.removeAttribute('data-hidden');
        } else {
          hSpan.innerHTML = '0';
          hSpan.setAttribute('data-hidden', '');
        }
      }
    });
  }

  updateCookie(count: number) {
    this.cookieHandler.setCookie(COOKIE_NAME, `${count}`, 1);
  }

  deleteCookie() {
    this.cookieHandler.deleteCookie(COOKIE_NAME);
  }
}

export const wishlistHeaderCountService = new WishlistHeaderCountService(CookieHandlerInstance);
